import React from 'react';
import { cn } from '../../utils/cn';

const Card = React.forwardRef(({ 
  className, 
  variant = 'default',
  padding = 'default',
  shadow = 'default',
  hover = false,
  children,
  ...props 
}, ref) => {
  const variantClasses = {
    default: 'bg-card text-card-foreground border border-border',
    primary: 'bg-primary-50 text-primary-900 border border-primary-200',
    secondary: 'bg-secondary-50 text-secondary-900 border border-secondary-200',
    success: 'bg-success-50 text-success-900 border border-success-200',
    warning: 'bg-warning-50 text-warning-900 border border-warning-200',
    danger: 'bg-danger-50 text-danger-900 border border-danger-200',
    muted: 'bg-muted text-muted-foreground border border-border',
    ghost: 'bg-transparent border-0'
  };

  const paddingClasses = {
    none: 'p-0',
    sm: 'p-3',
    default: 'p-4 md:p-6',
    lg: 'p-6 md:p-8',
    xl: 'p-8 md:p-10'
  };

  const shadowClasses = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    default: 'shadow-elevation-2',
    md: 'shadow-elevation-3',
    lg: 'shadow-elevation-4',
    xl: 'shadow-elevation-5'
  };

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg transition-smooth',
        variantClasses?.[variant],
        paddingClasses?.[padding],
        shadowClasses?.[shadow],
        hover && 'hover:shadow-elevation-3 hover:-translate-y-0.5 cursor-pointer',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

const CardHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 mb-4', className)}
    {...props}
  >
    {children}
  </div>
));

CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-h4 font-heading font-semibold leading-none tracking-tight', className)}
    {...props}
  >
    {children}
  </h3>
));

CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, children, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-body-sm text-muted-foreground', className)}
    {...props}
  >
    {children}
  </p>
));

CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('', className)} {...props}>
    {children}
  </div>
));

CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center mt-4 pt-4 border-t border-border', className)}
    {...props}
  >
    {children}
  </div>
));

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
export default Card;